import React, { useState } from 'react';
import { Modal, Input, Button, message, Typography, Space } from 'antd';
import { SafetyOutlined, CheckCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';

const { Title, Text } = Typography;

// New Theme Colors
const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  accentDim: 'rgba(34, 211, 238, 0.1)',
  hover: '#1f2a37',
};

const SetupContainer = styled.div`
  padding: 16px;
  color: ${COLORS.textPrimary};
  
  h4.ant-typography {
    color: ${COLORS.textPrimary} !important;
    margin-bottom: 8px !important;
  }
  
  div.ant-typography {
    color: ${COLORS.textSecondary} !important;
  }
  
  strong {
    color: ${COLORS.accent} !important;
  }
`;

const MainContent = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
  
  @media (max-width: 500px) {
    flex-direction: column;
    align-items: center;
  }
`;

const QRCodeContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid ${COLORS.border};
  flex-shrink: 0;
`;

const VerifySection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: left;
  
  @media (max-width: 500px) {
    text-align: center;
    width: 100%;
  }
`;

const InstructionsContainer = styled.div`
  text-align: left;
  margin-top: 16px;
  padding: 12px;
  background: ${COLORS.bgPrimary};
  border-radius: 8px;
  border-left: 3px solid ${COLORS.accent};
  color: ${COLORS.textSecondary};
  font-size: 13px;
  
  h5.ant-typography {
    color: ${COLORS.textPrimary} !important;
    margin-bottom: 4px !important;
    font-size: 13px !important;
  }
  
  ol {
    color: ${COLORS.textSecondary};
    margin: 0;
    padding-left: 16px;
  }
  
  li {
    margin-bottom: 2px;
  }
`;

const StyledInput = styled(Input)`
  background-color: ${COLORS.bgPrimary} !important;
  border-color: ${COLORS.border} !important;
  color: ${COLORS.textPrimary} !important;
  
  &:hover, &:focus {
    border-color: ${COLORS.accent} !important;
  }
  
  &::placeholder {
    color: #64748B;
  }
  
  .anticon {
    color: ${COLORS.textSecondary};
  }
`;

interface TwoFactorSetupProps {
  visible: boolean;
  qrCode: string;
  uri?: string;
  secret: string;
  onVerify: (code: string) => Promise<void>;
  onCancel?: () => void;
}

const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({
  visible,
  qrCode,
  uri,
  secret,
  onVerify,
  onCancel,
}) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVerify = async () => {
    if (!code || code.length !== 6) {
      message.error('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    try {
      await onVerify(code);
      message.success('2FA enabled successfully!');
      setCode('');
    } catch (error: any) {
      message.error(error.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <SafetyOutlined style={{ color: COLORS.accent }} />
          <span style={{ color: COLORS.textPrimary }}>Enable Two-Factor Authentication</span>
        </Space>
      }
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={520}
      closable={!loading}
      maskClosable={false}
      bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      className="dark-modal"
    >
      <SetupContainer>
        <MainContent>
          <QRCodeContainer>
            {uri ? (
              <QRCodeSVG
                value={uri}
                size={160}
                level="M"
                includeMargin={false}
              />
            ) : (
              <img 
                src={qrCode} 
                alt="QR Code" 
                style={{ width: '160px', height: '160px' }}
                onError={(e) => {
                  if (uri) {
                    console.error('QR code image failed to load');
                  }
                }}
              />
            )}
          </QRCodeContainer>

          <VerifySection>
            <Title level={4} style={{ marginTop: 0 }}>Scan & Verify</Title>
            <Text style={{ color: COLORS.textSecondary, display: 'block', marginBottom: 12, fontSize: 13 }}>
              Scan the QR code with your authenticator app, then enter the 6-digit code.
            </Text>
            
            <StyledInput
              size="large"
              placeholder="000000"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              onPressEnter={handleVerify}
              style={{ textAlign: 'center', fontSize: 24, letterSpacing: 10, fontWeight: 600 }}
            />
            <Button
              type="primary"
              size="large"
              block
              icon={<CheckCircleOutlined />}
              onClick={handleVerify}
              loading={loading}
              disabled={code.length !== 6}
              style={{ 
                marginTop: 12,
                background: COLORS.accent,
                borderColor: COLORS.accent,
                color: COLORS.bgPrimary,
                fontWeight: 600
              }}
            >
              Verify & Enable 2FA
            </Button>

            <div style={{ marginTop: 12 }}>
              <Text style={{ color: COLORS.textSecondary, fontSize: 12 }}>Manual key:</Text>
              <div style={{ fontFamily: 'monospace', fontSize: 11, color: COLORS.accent, background: COLORS.bgPrimary, padding: '6px 8px', borderRadius: '4px', border: `1px solid ${COLORS.border}`, marginTop: 4, wordBreak: 'break-all' }}>
                {secret}
              </div>
            </div>
          </VerifySection>
        </MainContent>

        <InstructionsContainer>
          <Title level={5}>Instructions:</Title>
          <ol>
            <li>Download an authenticator app (Google Authenticator, Authy)</li>
            <li>Tap "Add Account" or "+" and scan the QR code</li>
            <li>Enter the 6-digit code from the app above</li>
          </ol>
        </InstructionsContainer>
      </SetupContainer>
    </Modal>
  );
};

export default TwoFactorSetup;

